import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from 'initializers/types';

import { fetchRoleLaborTotals } from 'features/scheduleBuilder/slices/roleLaborTotals/slice';

import {
  captureHydrationException,
  getSliceState,
  selectAttributeForEntity,
} from '../helpers';

import { SLICE_NAME } from './constants';
import {
  ShiftLaborTotals,
  ShiftLaborTotalsSerializedEntity,
  ShiftLaborTotalsState,
} from './types';

export const shiftLaborTotalsAdapter = createEntityAdapter<ShiftLaborTotals>();

export const initialState: ShiftLaborTotalsState = {
  ...shiftLaborTotalsAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    removeAll: shiftLaborTotalsAdapter.removeAll,
  },
  extraReducers: builder => {
    builder.addCase('resources/post/pending', (state, action: any) => {
      if (action.meta?.arg?.name === 'roleLaborTotals') {
        state.status = 'pending';
      }
    });
    builder.addCase(fetchRoleLaborTotals.fulfilled, (state, action) => {
      state.status = 'succeeded';
      if (action?.payload?.included) {
        try {
          const items = (action.payload.included as any[])
            .filter(
              (entity): entity is ShiftLaborTotalsSerializedEntity =>
                entity.type === 'shiftLaborTotals'
            )
            .map(entity => ({
              id: entity.id,
              type: entity.type,
              hasOvertime: entity.attributes.hasOvertime,
              shift: entity.relationships?.shift?.data,
            }));
          shiftLaborTotalsAdapter.setAll(state, items);
        } catch (error) {
          captureHydrationException(state, error, SLICE_NAME);
        }
      }
    });
  },
});

export const { reducer, actions } = slice;

export const {
  selectById: selectShiftLaborTotalsById,
  selectIds: selectShiftLaborTotalsIds,
  selectEntities: selectShiftLaborTotalsEntities,
  selectAll: selectAllShiftLaborTotals,
  selectTotal: selectTotalShiftLaborTotals,
} = shiftLaborTotalsAdapter.getSelectors<RootState>(
  state => getSliceState(state)?.[SLICE_NAME] || initialState
);

export const selectShiftLaborTotalsStatus = (state: RootState) =>
  getSliceState(state)?.[SLICE_NAME]?.status || 'idle';

export const selectShiftLaborTotalsFetchIsPending = (
  state: RootState
): boolean => selectShiftLaborTotalsStatus(state) === 'pending';

export const selectShiftLaborTotalsAttribute = <
  T extends keyof ShiftLaborTotals
>({
  state,
  id,
  attribute,
}: {
  state: RootState;
  id: EntityId;
  attribute: T;
}) =>
  selectAttributeForEntity({
    state,
    attribute,
    entitySelector: selectShiftLaborTotalsById,
    id,
  });
